import React, { useEffect, useState } from 'react';
import { Typography, List, ListItem, InputAdornment, IconButton, ListItemText, Box, TextField, Divider } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import InventoryItemSearch from './InventoryItemSearch';
import { removeZeroDecimals } from '../../../utils/removeZeroDecimals';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../../context/SnackbarContext';
import TransactionTypeSelect from '../../inventory/controls/TransactionTypeSelect';
import { Stack } from '@mui/material';
import { getInventoryTransactionTypes } from '../../../api/inventoryService';
import { useCreateInventoryTransaction } from '../../../hooks/inventory/useCreateInventoryTransaction';
import ConfirmDialog from '../../common/modals/ConfirmDialog';
import { truncateString } from '../../../utils/truncateString';
import ItemOrBundleSelect from '../../inventory/modals/ItemOrBundleSelect';
import InventoryBundleSearch from './InventoryBundleSearch';

export default function ShoppingCart({successCallback, open, onClose, history, historyEntry, pet}) { 
  const { t } = useTranslation('common'); 
  const { showSnackbar } = useSnackbar();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleClose = () => {
    setCartItems([]);
    onClose();
  };

  const [defaultTransactionType, setDefaultTransactionType] = useState(null);

  const fetchDefaultTransactionType = async () => {
    const transactionType = await getInventoryTransactionTypes(1, 1, "administered");
    setDefaultTransactionType(transactionType.data[0].id);
  };

  const [itemOrBundleSelectOpen, setItemOrBundleSelectOpen] = useState(false);
  const [itemSearchOpen, setItemSearchOpen] = useState(false);
  const [bundleSearchOpen, setBundleSearchOpen] = useState(false);

  const [cartItems, setCartItems] = useState([]);

  const updateCartItem = (id, field, value) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const addToCart = (newItem) => {
    setCartItems((prevItems) => {
      const exists = prevItems.some(item => item.id === newItem.id);
      if (exists) {
        showSnackbar(t('cart.messages.duplicate_item'), "warning");
        return prevItems; // no change
      }

      // setCheckoutNotReady(false);
      showSnackbar(t('cart.messages.add_success'), "success");
      setItemSearchOpen(false);
      // setCheckoutNotReady(true);

      // Respect incoming quantity if provided, otherwise default to ""
      const itemWithExtras = {
        ...newItem,
        quantity: newItem.quantity ?? "", 
        transactionType: defaultTransactionType,
      };

      return [...prevItems, itemWithExtras];
    });
  };


  const addBundleToCart = (bundle) => {
    if (!bundle || !Array.isArray(bundle.items)) {
      console.error("Invalid bundle data");
      return;
    }

    bundle.items.forEach(bundleItem => {
      const { inventoryItem, quantity } = bundleItem;

      if (!inventoryItem) {
        console.warn("Bundle item missing inventoryItem:", bundleItem);
        return;
      }

      // Pass inventoryItem into addToCart, but inject the bundle quantity
      addToCart({
        ...inventoryItem,
        quantity, // use bundle-defined quantity
      });
    });
  };

  const removeFromCart = (itemId) => {
    setCartItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemId)
    );
    showSnackbar(t('cart.messages.remove_success'), "success");
  };

  const handleAddItemClick = () => {
    setItemOrBundleSelectOpen(true);
    // setItemSearchOpen(true);
  };

  const handleSingleItemClick = () => {
    setItemOrBundleSelectOpen(false);
    setItemSearchOpen(true);
  };

  const handleBundleClick = () => {
    setItemOrBundleSelectOpen(false);
    setBundleSearchOpen(true);
  };

  const validateQuantity = () => {
    let violations = 0;
    cartItems.forEach(item => {
      const qty = Number(item.quantity); // convert here

      if (item.quantity === "") {
        violations++;
      }
      if (qty === 0) {
        violations++;
      }
      if (qty > item.current_stock) {
        violations++;
      }
    });
    if (violations) {
      setCheckoutNotReady(true);
    } else {
      setCheckoutNotReady(false);
    }
  };

  const { create } = useCreateInventoryTransaction(successCallback);

  const [checkoutNotReady, setCheckoutNotReady] = useState(true);

  const handleCheckout = async () => {
    
    setConfirmDialogOpen(false);
    
    // extra validation before proceeding
    for (const item of cartItems) {
      const qty = Number(item.quantity);

      if (item.quantity === "" || qty === 0) {
        showSnackbar(t('cart.messages.quantity_empty'), "error");
        return; // stop checkout
      }

      if (qty > item.current_stock) {
        showSnackbar(t('cart.messages.not_enough_stock', { name: `${item.name}` }), "error");
        return; // stop checkout
      }
    }

    const payload = cartItems.map(item => ({
      type_id: item.transactionType,
      history_id: history.id,
      history_entry_id: historyEntry ? historyEntry.id : null,
      pet_id: pet.id,
      flow: "out",
      item_id: item.id,
      supplier_id: item.supplier_id ? item.supplier_id : null,
      quantity: Number(item.quantity), // convert here
      date: new Date().toLocaleDateString("en-CA")
    }));

    try { 
        await create(payload);
        setCartItems([]);
        showSnackbar(t('cart.messages.create_success'), "success");
    } catch (err) {
        console.log("err.response", err.response);
        showSnackbar(t('cart.messages.save_fail'), "error");
    }
  }

  useEffect(() => {
    validateQuantity();
  }, [cartItems]);

  useEffect(() => {
    if (open) {
      fetchDefaultTransactionType();
      setCheckoutNotReady(true);
    }
  }, [open]);

  return (
    <React.Fragment>
      <Dialog onClose={handleClose} open={open} disableRestoreFocus>
        <DialogTitle>Inventory Shopping Cart</DialogTitle>
        <DialogContent dividers>
          <List sx={{ minHeight: 500 }}>
            {cartItems?.map((item) => (
              <React.Fragment key={item.id}>
                <ListItem
                  secondaryAction={
                    <Box sx={{ ml: 2, display: 'flex', alignItems: 'right' }}>
                      <IconButton>
                        <DeleteIcon 
                          onClick={() => {removeFromCart(item.id)}}
                          fontSize='medium' 
                        />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={`${item.name}`}
                    secondary={ 
                      <>
                        <Typography component="span" variant="body2" color="text.primary">
                        {`${removeZeroDecimals(item.current_stock)}${item.uom} in stock`}
                        </Typography><br />
                        <Typography component="span" variant="body2" color="text.secondary">
                        {item.category.name}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
                <Stack direction="row" spacing={0.5}>
                  <TransactionTypeSelect 
                    label='Transaction Type'
                    size="small"
                    value={item.transactionType}
                    onChange={(e) => updateCartItem(item.id, "transactionType", e.target.value)}
                    sx={{width: "50%"}}
                  />
                  <TextField
                    value={item.quantity}
                    onChange={(e) => {
                      const val = e.target.value; // keep raw string
                      updateCartItem(item.id, "quantity", val);
                    }}
                    size="small"
                    variant="outlined"
                    type="number"
                    placeholder="Qty"
                    sx={{ width: 150, mr: 1 }}
                    error={Number(item.quantity) > item.current_stock}
                    helperText={
                      Number(item.quantity) > item.current_stock
                        ? `Not enough stock`
                        : ""
                    }
                    slotProps={{
                      input: {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant='body2'>
                            {truncateString(item.uom, 6)}
                            </Typography>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />

                </Stack>
                <Divider sx={{ my: 2 }}/>
              </React.Fragment>
              ))}
              {cartItems.length === 0 ?
              <ListItem sx={{ justifyContent: "center" }}>
                <Typography component="span" variant="body2" color="text.primary">
                  no items
                </Typography>
              </ListItem>
              : null }
          </List>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleAddItemClick}
          >
            Add Item
          </Button>
          <div style={{flex: '1 0 0'}} />
          <Button 
            disabled={checkoutNotReady} 
            onClick={() => setConfirmDialogOpen(true)}
            startIcon={<CheckIcon />}
          >
            CheckOut
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      <InventoryItemSearch 
        addButtonCallback={addToCart}
        open={itemSearchOpen}
        onClose={() => {setItemSearchOpen(false)}}
      />
      <InventoryBundleSearch 
        addButtonCallback={addBundleToCart}
        onSingleClick={handleBundleClick}
        open={bundleSearchOpen}
        onClose={() => {setBundleSearchOpen(false)}}
      />
      <ItemOrBundleSelect 
        onSingleClick={handleSingleItemClick}
        onBundleClick={handleBundleClick}
        open={itemOrBundleSelectOpen}
        onClose={() => {setItemOrBundleSelectOpen(false)}}
      />
      <ConfirmDialog
        open={confirmDialogOpen}
        title="Confirm Checkout"
        content={t('cart.messages.checkout_confirm')}
        confirmText="Proceed"
        cancelText="Cancel"
        onConfirm={handleCheckout}
        onCancel={() => setConfirmDialogOpen(false)}
      />
    </React.Fragment>
  );
}